import { gql } from '@apollo/client';
import moment from 'moment';

import { round } from '../utils/math';
import { query } from '../apollo/ApolloClient';

export interface MetricsQueryResponse {
  result: MetricsQueryResult[];
}

export interface MetricsQueryResult {
  labels: Record<string, string>;
  values: TimestampValue[];
}

export interface TimestampValue {
  timestamp: string;
  value: number;
}

const TIMESERIES_QUERY = gql`
  query timeseries(
    $startTime: Time!
    $endTime: Time!
    $metricsName: String!
    $stepSec: Int
    $selectors: MapString!
    $useRate: Boolean!
  ) {
    timeseries(
      startTime: $startTime
      endTime: $endTime
      metricsName: $metricsName
      stepSec: $stepSec
      selectors: $selectors
      useRate: $useRate
    ) {
      result {
        values {
          timestamp
          value
        }
      }
    }
  }
`;

const TIME_INTERVAL_SEC = 60;

export const getDatapoints = (
  startTime: string,
  endTime: string,
  datapoints: TimestampValue[]
): (number | null)[][] => {
  const dataArr = [];

  const counterStart = moment(startTime).startOf('minute');
  const counterEnd = moment(endTime).startOf('minute');
  let j = 0;
  for (
    let dateCounter = counterStart.clone();
    dateCounter.isBefore(counterEnd);
    dateCounter.add(TIME_INTERVAL_SEC, 's')
  ) {
    if (j < datapoints.length && moment(datapoints[j].timestamp).isSame(dateCounter)) {
      dataArr.push([moment(datapoints[j].timestamp).valueOf(), round(datapoints[j].value, 2)]);
      j += 1;
    } else {
      dataArr.push([dateCounter.valueOf(), null]);
    }
  }
  return dataArr;
};

export const getTimeseries = async (
  startTime: string,
  endTime: string,
  metricsName: string,
  selectors: Record<string, string>
): Promise<(number | null)[][] | null> => {
  try {
    const data = await query<Record<string, MetricsQueryResponse>>({
      query: TIMESERIES_QUERY,
      variables: {
        startTime,
        endTime,
        metricsName,
        stepSec: TIME_INTERVAL_SEC,
        selectors,
        useRate: true,
      },
    });
    if (
      data &&
      data.timeseries &&
      data.timeseries.result &&
      data.timeseries.result.length !== 0 &&
      data.timeseries.result[0] &&
      data.timeseries.result[0].values
    ) {
      return getDatapoints(startTime, endTime, data.timeseries.result[0].values);
    }
  } catch (error) {
    console.log('There was an error while fetching timeseries data: ', JSON.stringify(error));
  }
  return null;
};
